/**
 * Token context menu for turning a selection into a typed token.
 *
 * Inspired by SuggestionQuickMenu.
 */

import {EditorView} from "@codemirror/view";
import {TokenType} from "../models/editor.types";
import {tokensField, setTokensEffect} from "../state/editor.state";
import {clearTokensInRange, overrideTokensInRange} from "../utils/tokens.utils";

const TOKEN_MENU_ITEMS: Array<{ type: TokenType; label: string }> = [
  {type: "quantity", label: "Quantity"},
  {type: "ingredient", label: "Ingredient"},
];

export class TokenContextMenu {
  private menuElement: HTMLElement | null = null;
  private view: EditorView | null = null;
  private range: { from: number; to: number } | null = null;

  show(view: EditorView, range: { from: number; to: number }, anchorRect: DOMRect | null): void {
    this.hide();

    this.view = view;
    this.range = range;

    const menu = document.createElement("div");
    menu.className = "token-context-menu";

    Object.assign(menu.style, {
      display: 'flex',
      gap: '4px',
      padding: '4px',
      background: '#2b2b2b',
      border: '1px solid #3a3a3a',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      zIndex: '100000',
      position: 'fixed'
    });

    for (const item of TOKEN_MENU_ITEMS) {
      menu.appendChild(this.createTypeButton(item.type, item.label));
    }

    menu.appendChild(this.createClearButton());

    // Position near selection
    const rect = anchorRect;
    if (rect) {
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.left = `${rect.left}px`;
    } else {
      // Fallback top-left-ish
      menu.style.top = `8px`;
      menu.style.left = `8px`;
    }

    document.body.appendChild(menu);
    this.menuElement = menu;

    setTimeout(() => {
      document.addEventListener("click", this.handleOutsideClick);
      window.addEventListener("scroll", this.hide, true);
      window.addEventListener("resize", this.hide);
    }, 0);
  }

  private createTypeButton(type: TokenType, label: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = `token-menu-btn token-menu-btn-${type}`;
    btn.title = `Mark selection as ${label}`;
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.applyType(type);
    };

    Object.assign(btn.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '32px',
      padding: '0 10px',
      border: 'none',
      borderRadius: '6px',
      background: 'transparent',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'background 0.2s ease',
      fontSize: '12px',
      fontWeight: '600'
    });

    btn.onmouseenter = () => btn.style.background = 'rgba(255, 255, 255, 0.1)';
    btn.onmouseleave = () => btn.style.background = 'transparent';

    btn.textContent = label;
    return btn;
  }

  private createClearButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "token-menu-btn token-menu-btn-clear";
    btn.title = "Clear tokens in selection";
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.clear();
    };

    Object.assign(btn.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: '0',
      border: 'none',
      borderRadius: '6px',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'background 0.2s ease'
    });

    btn.onmouseenter = () => btn.style.background = 'rgba(255, 255, 255, 0.1)';
    btn.onmouseleave = () => btn.style.background = 'transparent';

    const icon = document.createElement("span");
    icon.textContent = "×";

    Object.assign(icon.style, {
      fontSize: '18px',
      lineHeight: '1',
      color: '#f44336',
      fontWeight: '700'
    });

    btn.appendChild(icon);
    return btn;
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    if (this.menuElement && !this.menuElement.contains(e.target as Node)) {
      this.hide();
    }
  };

  hide = (): void => {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null;
    }

    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.hide, true);
    window.removeEventListener("resize", this.hide);

    this.view = null;
    this.range = null;
  };

  private applyType(type: TokenType): void {
    if (!this.view || !this.range) return;

    const tokens = this.view.state.field(tokensField);
    const next = overrideTokensInRange(tokens, this.range, type);

    this.view.dispatch({effects: setTokensEffect.of(next)});
    setTimeout(() => this.hide(), 10);
  }

  private clear(): void {
    if (!this.view || !this.range) return;

    const tokens = this.view.state.field(tokensField);
    const next = clearTokensInRange(tokens, this.range);

    this.view.dispatch({effects: setTokensEffect.of(next)});
    setTimeout(() => this.hide(), 10);
  }
}

export const tokenContextMenu = new TokenContextMenu();
